﻿import React, { useEffect, useState, ChangeEvent, MouseEvent, ReactNode } from 'react';
import { useTheme } from '@mui/material/styles';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Dialog from '@mui/material/Dialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import Button from '@mui/material/Button';
import ArticleIcon from '@mui/icons-material/Article';
import ImageIcon from '@mui/icons-material/Image';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import PeopleIcon from '@mui/icons-material/People';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PersonIcon from '@mui/icons-material/Person';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TooltipText from '../../../../../../shared/feedback/TooltipText';
import SearchInputBase from './SearchInputBase';
import history from '../../../../../../shared/history';
import { impersonate } from '../../../../../../actions/userActions';
import { TranslatedLabels } from '../../../../../../shared/types';
import { Translate } from '../../../../../../shared/LanguageService';
import { useTelemetryContext } from '../../../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../../../telemetry/utils';
import { ISearchResult } from '../../../../../../features/search/Entities';
import { useUserContext } from '../../../../../../shared/context/user-context';
import { useDebounce } from '../../../../../../shared/hooks/debounce';
import { selectIsImpersonating } from '../../../../../../reducers/userReducer';
import { Spacer } from '../../Spacer';
import { useSearch } from './useSearch';

type ReduxProps = {
    isImpersonating: boolean;
};

type ReduxActions = {
    impersonateUser: (userId: number) => Promise<void>;
};

type ExternalProps = {};

type AdvancedNavbarSearchProps = {
    labels: TranslatedLabels;
} & ReduxActions &
    ReduxProps;

enum SearchOptionType {
    BrokerAgent = 'BrokerAgent',
    BrokerFirm = 'BrokerFirm',
    GenericOrder = 'GenericOrder',
    PlanOrder = 'PlanOrder',
    PhotoOrder = 'PhotoOrder',
    DigitalStyling = 'DigitalStyling',
    Photographer = 'Photographer',
    Unknown = 'Unknown'
}

type OrderType = 'order' | 'plan' | 'photo' | 'digital-styling';

type SearchOption = {
    id: number;
    brokerFirm?: string;
    impersonationId?: number;
    label: string;
    type: SearchOptionType;
};

type SearchResultOptionProps = {
    icon: ReactNode;
    label: ReactNode;
    actionButton?: ReactNode;
    onClick?: () => void;
};

type SearchOptionBrokerAgentProps = {
    brokerFirm: string;
    label: string;
    id: number;
    impersonationId: number;
    impersonateUser: (userId: number) => Promise<void>;
    onClick: () => void;
};

type ImpersonateUserFn = (userId: number) => Promise<void>;

const SEARCH_DEBOUNCE_DELAY = 300;

function StyledPopper(props) {
    return (
        <Popper
            {...props}
            style={{ minWidth: '234px', width: 'fit-content' }}
            placement="bottom-end"
        />
    );
}

const StyledImpersonateButton = styled(Button)({
    width: '24px',
    height: '32px',
    padding: '6px',
    marginLeft: '8px',
    backgroundColor: '#e0e0e0',
    '&:hover': {
        backgroundColor: '#c9c9c9'
    }
});

function ImpersonateButton({ children, ...props }) {
    let labels: TranslatedLabels = {
        tooltip: Translate('impersonation/button-label/impersonate')
    };

    return (
        <Tooltip
            title={<TooltipText variant="body1">{labels.tooltip}</TooltipText>}
            placement="right-start"
        >
            <StyledImpersonateButton {...props}>{children}</StyledImpersonateButton>
        </Tooltip>
    );
}

function SearchResultOption({ icon, label, actionButton, onClick }: SearchResultOptionProps) {
    function handleClick() {
        if (onClick) {
            onClick();
        }
    }

    return (
        <Box
            sx={{
                display: 'flex',
                minHeight: '36px',
                padding: '8px 20px',
                cursor: 'pointer',
                alignItems: 'center',
                '&:hover': {
                    backgroundColor: '#f5f5f5'
                }
            }}
            onClick={handleClick}
        >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {icon}
                {label}
            </Box>
            {actionButton ? (
                <>
                    <Box sx={{ flexGrow: 1 }} />
                    {actionButton}
                </>
            ) : null}
        </Box>
    );
}

function handleClickImpersonateFactory(userId: number, impersonateUser: ImpersonateUserFn) {
    let { logger } = useTelemetryContext();
    let { user } = useUserContext();

    return function handler(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();

        impersonateUser(userId)
            .then(() => {
                history.push('/');
            })
            .catch((error) => {
                let additionalInfo = {
                    origin: `Impersonate user: ${userId} - from AdvancedNavbarSearch`,
                    userInfo: getUserInfo(user)
                };
                logger.logException(error, additionalInfo);
            });
    };
}

function SearchResultOptionLabel({ children }) {
    return (
        <Typography
            variant="body1"
            sx={{ fontSize: '16px', maxWidth: '360px', paddingLeft: '12px' }}
        >
            {children}
        </Typography>
    );
}

function SearchOptionBrokerAgent({
    label,
    brokerFirm,
    id,
    impersonationId,
    impersonateUser,
    onClick
}: SearchOptionBrokerAgentProps) {
    let onClickImpersonateUser = handleClickImpersonateFactory(impersonationId, impersonateUser);

    function handleClick() {
        onClick();
        history.push(`/brokers/${id}`);
    }

    function handleClickImpersonate(event: MouseEvent) {
        onClick();
        onClickImpersonateUser(event);
    }

    return (
        <SearchResultOption
            icon={<PersonIcon color="action" sx={{ width: '24px', height: '24px' }} />}
            label={
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <SearchResultOptionLabel>{label}</SearchResultOptionLabel>
                    <Typography
                        variant="caption"
                        sx={{ fontSize: '12px', maxWidth: '360px', paddingLeft: '12px' }}
                    >
                        {brokerFirm}
                    </Typography>
                </Box>
            }
            onClick={handleClick}
            actionButton={
                <ImpersonateButton onClick={handleClickImpersonate}>
                    <PeopleIcon color="primary" sx={{ width: '20px', height: '14px' }} />
                </ImpersonateButton>
            }
        />
    );
}

function SearchOptionBrokerFirm({ label, id, onClick }) {
    function handleClick() {
        onClick();
        history.push(`/brokers/${id}`);
    }

    return (
        <SearchResultOption
            icon={<LocationCityIcon color="action" sx={{ width: '24px', height: '24px' }} />}
            label={<SearchResultOptionLabel>{label}</SearchResultOptionLabel>}
            onClick={handleClick}
        />
    );
}

function SearchOptionGenericOrder({ label, id, onClick }) {
    function handleClick() {
        onClick();
        history.push(`/orders/${id}/basic`);
    }

    return (
        <SearchResultOption
            icon={<ArticleIcon color="action" sx={{ width: '24px', height: '24px' }} />}
            label={<SearchResultOptionLabel>{label}</SearchResultOptionLabel>}
            onClick={handleClick}
        />
    );
}

function SearchOptionPhotoOrder({ label, id, onClick }) {
    function handleClick() {
        onClick();
        history.push(`/orders/${id}/image`);
    }

    return (
        <SearchResultOption
            icon={<ImageIcon color="action" sx={{ width: '24px', height: '24px' }} />}
            label={<SearchResultOptionLabel>{label}</SearchResultOptionLabel>}
            onClick={handleClick}
        />
    );
}

function SearchOptionPlanOrder({ label, id, onClick }) {
    function handleClick() {
        onClick();
        history.push(`/orders/${id}/plan`);
    }

    return (
        <SearchResultOption
            icon={<DesignServicesIcon color="action" sx={{ width: '24px', height: '24px' }} />}
            label={<SearchResultOptionLabel>{label}</SearchResultOptionLabel>}
            onClick={handleClick}
        />
    );
}

function SearchOptionDigitalStylingOrder({ label, id, onClick }) {
    function handleClick() {
        onClick();
        history.push(`/orders/${id}/digital-styling`);
    }

    return (
        <SearchResultOption
            icon={<LaptopMacIcon color="action" sx={{ width: '24px', height: '24px' }} />}
            label={<SearchResultOptionLabel>{label}</SearchResultOptionLabel>}
            onClick={handleClick}
        />
    );
}

function SearchOptionPhotographer({ label, id, onClick, impersonationId, impersonateUser }) {
    let onClickImpersonateUser = handleClickImpersonateFactory(impersonationId, impersonateUser);

    function handleClickImpersonate(event: MouseEvent) {
        onClick();
        onClickImpersonateUser(event);
    }

    function handleClick() {
        onClick();
        history.push(`/photographers/${id}`);
    }

    return (
        <SearchResultOption
            icon={<PhotoCameraIcon color="action" sx={{ width: '24px', height: '24px' }} />}
            label={<SearchResultOptionLabel>{label}</SearchResultOptionLabel>}
            onClick={handleClick}
            actionButton={
                <ImpersonateButton onClick={handleClickImpersonate}>
                    <PeopleIcon color="primary" sx={{ width: '20px', height: '14px' }} />
                </ImpersonateButton>
            }
        />
    );
}

function getSearchOptionType(orderType: OrderType): SearchOptionType {
    switch (orderType) {
        case 'order': {
            return SearchOptionType.GenericOrder;
        }
        case 'photo': {
            return SearchOptionType.PhotoOrder;
        }
        case 'plan': {
            return SearchOptionType.PlanOrder;
        }
        case 'digital-styling': {
            return SearchOptionType.DigitalStyling;
        }
        default: {
            return SearchOptionType.Unknown;
        }
    }
}

function transformToSearchResult(data: undefined | ISearchResult): SearchOption[] {
    let brokerAgents = data
        ? data.brokerAgents.map(({ name, id, brokerAgentId, brokerFirm }) => ({
              id,
              brokerFirm,
              impersonationId: brokerAgentId,
              label: name,
              type: SearchOptionType.BrokerAgent
          }))
        : [];
    let brokerFirms = data
        ? data.brokerFirms.map(({ name, id }) => ({
              id,
              label: name,
              type: SearchOptionType.BrokerFirm
          }))
        : [];
    let orders = data
        ? data.orders.map(({ id, orderNumber, address, type }) => ({
              id,
              label: address ? `${orderNumber} - ${address}` : orderNumber,
              type: getSearchOptionType(type as OrderType)
          }))
        : [];
    let photographers = data
        ? data.photographers.map(({ id, name }) => ({
              id,
              label: name,
              type: SearchOptionType.Photographer
          }))
        : [];

    return [].concat(brokerAgents).concat(photographers).concat(orders).concat(brokerFirms);
}

function useSearchResult(
    searchPhrase: string,
    data: ISearchResult | null,
    isImpersonating: boolean
): SearchOption[] {
    let [searchResult, setSearchResult] = useState<SearchOption[]>([]);

    useEffect(() => {
        if (searchResult.length > 0 && searchPhrase.length < 3) {
            setSearchResult([]);
        }
    }, [searchResult, searchPhrase]);

    useEffect(() => {
        setSearchResult([]);
    }, [isImpersonating]);

    useEffect(() => {
        setSearchResult(data ? transformToSearchResult(data) : []);
    }, [data]);

    return searchResult;
}

export function AdvancedNavbarSearchLargerScreens({
    impersonateUser,
    isImpersonating,
    labels
}: AdvancedNavbarSearchProps) {
    let [searchPhrase, setSearchPhrase] = useState<string>('');
    let debouncedSearchPhrase = useDebounce(searchPhrase, SEARCH_DEBOUNCE_DELAY);

    function handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
        setSearchPhrase(event.target.value.trimStart());
    }

    function clearSearch() {
        setSearchPhrase('');
    }

    function handleImpersonateUser(userId: number) {
        return impersonateUser(userId);
    }

    function handleSearchBlur() {
        clearSearch();
    }

    let { isLoading, data } = useSearch(searchPhrase, debouncedSearchPhrase);
    let searchResult = useSearchResult(searchPhrase, data, isImpersonating);

    let hideNoResultsFoundPopper = searchPhrase.length > 2 && !isLoading && data ? false : true;

    return (
        <>
            <Spacer />
            <Autocomplete
                key={debouncedSearchPhrase}
                options={searchResult}
                getOptionLabel={(option: SearchOption) => option.label}
                disableListWrap
                freeSolo={hideNoResultsFoundPopper}
                openOnFocus
                loading={isLoading}
                loadingText={labels.loadingText}
                noOptionsText={labels.noOptionsText}
                PopperComponent={StyledPopper}
                renderOption={(props, option: SearchOption) => {
                    switch (option.type) {
                        case SearchOptionType.BrokerAgent: {
                            return (
                                <SearchOptionBrokerAgent
                                    key={option.id}
                                    label={option.label}
                                    brokerFirm={option.brokerFirm}
                                    impersonationId={option.impersonationId}
                                    id={option.id}
                                    impersonateUser={handleImpersonateUser}
                                    onClick={() => {
                                        clearSearch();
                                    }}
                                />
                            );
                        }
                        case SearchOptionType.BrokerFirm: {
                            return (
                                <SearchOptionBrokerFirm
                                    key={option.id}
                                    label={option.label}
                                    id={option.id}
                                    onClick={() => {
                                        clearSearch();
                                    }}
                                />
                            );
                        }
                        case SearchOptionType.GenericOrder: {
                            return (
                                <SearchOptionGenericOrder
                                    key={option.id}
                                    id={option.id}
                                    label={option.label}
                                    onClick={() => {
                                        clearSearch();
                                    }}
                                />
                            );
                        }
                        case SearchOptionType.PhotoOrder: {
                            return (
                                <SearchOptionPhotoOrder
                                    key={option.id}
                                    id={option.id}
                                    label={option.label}
                                    onClick={() => {
                                        clearSearch();
                                    }}
                                />
                            );
                        }
                        case SearchOptionType.PlanOrder: {
                            return (
                                <SearchOptionPlanOrder
                                    key={option.id}
                                    id={option.id}
                                    label={option.label}
                                    onClick={() => {
                                        clearSearch();
                                    }}
                                />
                            );
                        }
                        case SearchOptionType.DigitalStyling: {
                            return (
                                <SearchOptionDigitalStylingOrder
                                    key={option.id}
                                    id={option.id}
                                    label={option.label}
                                    onClick={() => {
                                        clearSearch();
                                    }}
                                />
                            );
                        }
                        case SearchOptionType.Photographer: {
                            return (
                                <SearchOptionPhotographer
                                    key={option.id}
                                    id={option.id}
                                    impersonateUser={handleImpersonateUser}
                                    impersonationId={option.id}
                                    label={option.label}
                                    onClick={() => {
                                        clearSearch();
                                    }}
                                />
                            );
                        }
                        default: {
                            return null;
                        }
                    }
                }}
                renderInput={(params) => (
                    <SearchInputBase
                        ref={params.InputProps.ref}
                        inputProps={{ ...params.inputProps, autoFocus: true }}
                        value={searchPhrase}
                        onChange={handleSearchChange}
                        onBlur={handleSearchBlur}
                    />
                )}
            />
            <Spacer />
        </>
    );
}

export function AdvancedNavbarSearchSmallerScreens({
    impersonateUser,
    isImpersonating,
    labels
}: AdvancedNavbarSearchProps) {
    let [openSearch, setOpenSearch] = useState<boolean>(false);
    let [searchPhrase, setSearchPhrase] = useState<string>('');
    let debouncedSearchPhrase = useDebounce(searchPhrase, SEARCH_DEBOUNCE_DELAY);

    function handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
        setSearchPhrase(event.target.value);
    }

    function clearSearch() {
        setSearchPhrase('');
        setOpenSearch(false);
    }

    function handleImpersonateUser(userId: number) {
        setOpenSearch(false);
        return impersonateUser(userId);
    }

    let { isLoading, data } = useSearch(openSearch ? searchPhrase : '', debouncedSearchPhrase);
    let searchResult = useSearchResult(searchPhrase, data, isImpersonating);

    function handleClickSearch() {
        setOpenSearch(true);
    }

    function handleCloseSearch() {
        setOpenSearch(false);
    }

    let noMatchingSearchResult =
        data !== null && Object.values(data).every((current) => current.length === 0);

    return (
        <>
            <Tooltip
                title={<TooltipText variant="body1">{labels.searchTooltip}</TooltipText>}
                placement="bottom"
            >
                <IconButton onClick={handleClickSearch} size="medium" sx={{ color: '#fff' }}>
                    <SearchIcon sx={{ width: '24px', height: '24px' }} />
                </IconButton>
            </Tooltip>
            <Dialog onClose={handleCloseSearch} open={openSearch}>
                <DialogTitle>
                    <SearchInputBase
                        inputProps={{ autoFocus: true }}
                        value={searchPhrase}
                        onChange={handleSearchChange}
                    />
                </DialogTitle>
                {isLoading ? (
                    <DialogContent dividers>
                        <Typography variant="body1">{labels.loadingText}</Typography>
                    </DialogContent>
                ) : noMatchingSearchResult ? (
                    <DialogContent dividers>
                        <Typography variant="body1">{labels.noOptionsText}</Typography>
                    </DialogContent>
                ) : null}
                {searchResult.length > 0 ? (
                    <DialogContent dividers>
                        {searchResult.map((option) => {
                            switch (option.type) {
                                case SearchOptionType.BrokerAgent: {
                                    return (
                                        <SearchOptionBrokerAgent
                                            key={option.id}
                                            label={option.label}
                                            brokerFirm={option.brokerFirm}
                                            impersonationId={option.impersonationId}
                                            id={option.id}
                                            impersonateUser={handleImpersonateUser}
                                            onClick={() => {
                                                clearSearch();
                                            }}
                                        />
                                    );
                                }
                                case SearchOptionType.BrokerFirm: {
                                    return (
                                        <SearchOptionBrokerFirm
                                            key={option.id}
                                            label={option.label}
                                            id={option.id}
                                            onClick={() => {
                                                clearSearch();
                                            }}
                                        />
                                    );
                                }
                                case SearchOptionType.GenericOrder: {
                                    return (
                                        <SearchOptionGenericOrder
                                            key={option.id}
                                            id={option.id}
                                            label={option.label}
                                            onClick={() => {
                                                clearSearch();
                                            }}
                                        />
                                    );
                                }
                                case SearchOptionType.PhotoOrder: {
                                    return (
                                        <SearchOptionPhotoOrder
                                            key={option.id}
                                            id={option.id}
                                            label={option.label}
                                            onClick={() => {
                                                clearSearch();
                                            }}
                                        />
                                    );
                                }
                                case SearchOptionType.PlanOrder: {
                                    return (
                                        <SearchOptionPlanOrder
                                            key={option.id}
                                            id={option.id}
                                            label={option.label}
                                            onClick={() => {
                                                clearSearch();
                                            }}
                                        />
                                    );
                                }
                                case SearchOptionType.DigitalStyling: {
                                    return (
                                      <SearchOptionDigitalStylingOrder
                                        key={option.id}
                                        id={option.id}
                                        label={option.label}
                                        onClick={() => {
                                            clearSearch();
                                        }}
                                      />
                                    );
                                }
                                case SearchOptionType.Photographer: {
                                    return (
                                        <SearchOptionPhotographer
                                            key={option.id}
                                            id={option.id}
                                            impersonateUser={handleImpersonateUser}
                                            impersonationId={option.id}
                                            label={option.label}
                                            onClick={() => {
                                                clearSearch();
                                            }}
                                        />
                                    );
                                }
                                default: {
                                    return null;
                                }
                            }
                        })}
                    </DialogContent>
                ) : null}
            </Dialog>
        </>
    );
}

function AdvancedNavbarSearch(props: AdvancedNavbarSearchProps) {
    let theme = useTheme();
    let isSmallerScreen = useMediaQuery(theme.breakpoints.down('sm'));

    if (isSmallerScreen) {
        return <AdvancedNavbarSearchSmallerScreens {...props} />;
    }

    return <AdvancedNavbarSearchLargerScreens {...props} />;
}

function TranslatedAdvancedNavbarSearch(props: ReduxActions & ReduxProps) {
    let labels: TranslatedLabels = {
        loadingText: Translate('header/advanced-search/loading-text'),
        noOptionsText: Translate('header/advanced-search/no-results'),
        searchTooltip: Translate('header/nav/search')
    };

    return <AdvancedNavbarSearch {...props} labels={labels} />;
}

function mapStateToProps(state): ReduxProps {
    let isImpersonating = selectIsImpersonating(state);

    return {
        isImpersonating
    };
}

function mapDispatchToProps(dispatch: Dispatch<unknown>): ReduxActions {
    return {
        impersonateUser: (userId: number) => dispatch(impersonate(userId))
    };
}

export default connect<ReduxProps, ReduxActions, ExternalProps>(
    mapStateToProps,
    mapDispatchToProps
)(TranslatedAdvancedNavbarSearch);
